import {
  type FC,
  type JSX,
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useTheme, getStoreValue, Icon } from 'pixel-react';
import { useDispatch } from 'react-redux';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import BodyLoaderGuard from '@/components/BodyLoader';
import ChatBot from '@/components/chatbot/ChatBot';
import { Header } from '@/components/Header';
import { NotificationHeader } from '@/components/NotificationHeader';
import { NotificationHeaderIconList } from '@/config/constants';
import { useSectionLocks } from '@/hooks/useSectionLocks';
import { getPrivilegeStateData } from '@/lib/utils/commonUtils';
import { EnvironmentConfigurationRoutes } from '@/routes/EnvironementConfigurationRoutes';
import { ExecutionConfigRoutes } from '@/routes/ExecutionConfigRoutes';
import { IntegrationRoutes } from '@/routes/IntegrationRoutes';
import { ProjectManagementRoutes } from '@/routes/projectManagementRoutes';
import { TemplateRoutes } from '@/routes/TemplateRoutes';
import { UsersManagementRoutes } from '@/routes/UsersManagementRoutes';
import { RootState } from '@/store';
import { useLazyGetClientConfigurationQuery } from '@/store/EnvironmentConfiguration';
import { useAppSelector } from '@/store/hooks';
import { useLazyGetMockPrivilegesQuery } from '@/store/privilege/privilegeApi';
import { Restriction } from '@/store/privilege/privilegeApiTypes';
import { setHeaders, setRestrictions } from '@/store/privilege/privilegeSlice';

import styles from './Home.module.scss';
import type { IntegrationRoute } from './type';

const Suites = lazy(() => import('suites/App'));
const TestDev = lazy(() => import('testDev/App'));
const TestData = lazy(() => import('testData/App'));
const Defects = lazy(() => import('defects/App'));
const Webservice = lazy(() => import('webservice/App'));
const Repository = lazy(() => import('repository/App'));
const Projects = lazy(() => import('projects/App'));
const Licenses = lazy(() => import('licenses/App'));
// const ReportSettings = lazy(() => import('suites/reportSettings'));
const Dashboard = lazy(() => import('dashboard/App'));
const Help = lazy(() => import('help/App'));
const AutonomousBot = lazy(() => import('@/pages/AutonomousBot/AutonomousBot'));

const Home: FC = (): JSX.Element => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isUpgradeDrawerOpen, setIsUpgradeDrawerOpen] = useState(false);

  const [trigger] = useLazyGetClientConfigurationQuery();
  const dispatch = useDispatch();
  const privilegeData = useAppSelector((state: RootState) => state.privilege);
  const importLocks = useSectionLocks();
  const [isRefetchPrivilegeData, setIsRefetchPrivilegeData] =
    useState<boolean>(false);
  const [isProjectClicked, setIsProjectClicked] = useState<boolean>(false);
  const [isChatbotVisible, setIsChatbotVisible] = useState(false);
  const [getPrivilegeData] = useLazyGetMockPrivilegesQuery();
  const handleDownloadClientFile = (url: string) => {
    const fileName = url?.split('/').pop();
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || '';
    a.click();
    closeDownloadModal();
  };

  const { applyTheme } = useTheme();
  const location = useLocation();
  const isHelpmodule = location.pathname.split('/')[2] === 'help';
  const downloadClientRef = useRef<HTMLButtonElement>(null);
  const handleUpgrade = () => {
    setIsUpgradeDrawerOpen(true);
  };
  const closeUpgradeDrawer = () => {
    setIsUpgradeDrawerOpen(false);
  };
  const handleIconClick = async (icon: string) => {
    if (icon === 'download_icon') {
      setIsDownloadModalOpen(true);
    } else if (icon === 'nlp_help_icon') {
      window.open('/app/help', '_blank');
    } else if (icon === 'eclipse_plugin') {
      const response = await trigger('eclipse').unwrap();
      if (response?.responseCode === 200) {
        handleDownloadClientFile(response?.responseObject?.[0]?.url);
      }
    }
  };
  const closeDownloadModal = () => {
    setIsDownloadModalOpen(false);
  };

  useEffect(() => {
    const data = getStoreValue('current-theme');

    applyTheme(data === 'dark' ? 'ff-dark-theme' : 'ff-light-theme');
  }, [applyTheme]);

  const renderRoutes = (routes: IntegrationRoute[]) => {
    return routes.map((route: IntegrationRoute, index: number) => {
      return (
        <Route key={index} path={route.path} element={route.element}>
          {route.children && renderRoutes(route.children)}
        </Route>
      );
    });
  };
  const setPrivilegeHeaderData = (privilegeData: any) => {
    const currentProject = getStoreValue('current-project') as { id: string };

    const projectId = currentProject?.id;
    if (projectId === 'All Projects') {
      dispatch(setHeaders({ key: 'global', headers: privilegeData }));
    } else {
      dispatch(setHeaders({ key: projectId, headers: privilegeData }));
    }
  };
  const setPrivilegeRestrictionsData = (privilegeData: Restriction) => {
    const currentProject = getStoreValue('current-project') as { id: string };

    const projectId = currentProject?.id;
    if (projectId === 'All Projects') {
      dispatch(setRestrictions({ key: 'global', restrictions: privilegeData }));
    } else {
      dispatch(
        setRestrictions({ key: projectId, restrictions: privilegeData }),
      );
    }
  };
  const navigate = useNavigate();
  const updatePrivilegeData = async () => {
    const response = await getPrivilegeData().unwrap();
    if (isProjectClicked) {
      const hasTestDev =
        response?.responseObject?.projectLevels?.individualProjects?.headers.some(
          (header: { label: string }) => header.label === 'Test Dev',
        );

      if (hasTestDev) {
        navigate('/app/test-dev/scripts');
      } else {
        navigate('/app/projects/project');
      }
    }
    setIsRefetchPrivilegeData(false);
  };

  useEffect(() => {
    if (isRefetchPrivilegeData) {
      updatePrivilegeData();
    }
  }, [isRefetchPrivilegeData, isProjectClicked]);
  return (
    <>
      {' '}
      <div className={styles.home}>
        <Header />
        <div className={`${styles['home_body']}`} data-import-lock-region="">
          {!isHelpmodule && (
            <NotificationHeader
              iconsList={NotificationHeaderIconList}
              handleUpgrade={handleUpgrade}
              isUpgradeDrawerOpen={isUpgradeDrawerOpen}
              closeUpgradeDrawer={closeUpgradeDrawer}
              handleIconClick={handleIconClick}
              downloadClientRef={downloadClientRef}
              isDownloadModalOpen={isDownloadModalOpen}
              closeDownloadModal={closeDownloadModal}
            />
          )}
          {!isChatbotVisible ? (
            <div className={styles.chatbot}>
              <Icon
                name="arrow_left_accordian"
                height={16}
                width={16}
                color="var(--bg-primary)"
                onClick={() => {
                  setIsChatbotVisible(true);
                }}
              />
            </div>
          ) : (
            <Icon
              className={styles.chatbot_close}
              name="arrow_right"
              height={16}
              width={16}
              onClick={() => {
                setIsChatbotVisible(false);
              }}
            />
          )}

          <main
            className={!isHelpmodule ? `${styles.main}` : `${styles.main_help}`}
          >
            <Suspense fallback={<BodyLoaderGuard />}>
              <Routes>
                <Route
                  path="/projects/*"
                  element={
                    <Projects
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      privilegeDetails={privilegeData.privilegeDetails}
                      fetchPrivilegeData={setIsRefetchPrivilegeData}
                      setIsProjectClicked={setIsProjectClicked}
                    />
                  }
                />
                <Route
                  path="/suites/*"
                  element={
                    <Suites
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      privilegeDetails={privilegeData.privilegeDetails}
                      importLocks={importLocks}
                    />
                  }
                />
                <Route
                  path="/repository/*"
                  element={
                    <Repository
                      setPrivilegeHeaderData={setPrivilegeHeaderData}
                      setPrivilegeRestrictionsData={
                        setPrivilegeRestrictionsData
                      }
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      privilegeDetails={privilegeData.privilegeDetails}
                      importLocks={importLocks}
                    />
                  }
                />
                <Route
                  path="/test-dev/*"
                  element={
                    <TestDev
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      privilegeDetails={privilegeData.privilegeDetails}
                      setPrivilegeHeaderData={setPrivilegeHeaderData}
                      setPrivilegeRestrictionsData={
                        setPrivilegeRestrictionsData
                      }
                      importLocks={importLocks}
                    />
                  }
                />
                <Route
                  path="/defects/*"
                  element={
                    <Defects
                      privilegeData={getPrivilegeStateData(privilegeData)}
                    />
                  }
                />
                <Route
                  path="/webservice/*"
                  element={
                    <Webservice
                      privilegeData={getPrivilegeStateData(privilegeData)}
                    />
                  }
                />
                <Route
                  path="/licenses/*"
                  element={
                    <Licenses fetchPrivilegeData={setIsRefetchPrivilegeData} />
                  }
                />
                <Route
                  path="/test-data/*"
                  element={
                    <TestData
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      importLocks={importLocks}
                    />
                  }
                />
                <Route path="/help/*" element={<Help />} />
                <Route
                  path="/autonomous-testing/*"
                  element={<AutonomousBot />}
                />
                {EnvironmentConfigurationRoutes.map(
                  ({ path, element, children }) => (
                    <Route key={path} path={path} element={element}>
                      {children &&
                        children.map(
                          ({ path: childPath, element: childElement }) => (
                            <Route
                              key={childPath}
                              path={childPath}
                              element={childElement}
                            />
                          ),
                        )}
                    </Route>
                  ),
                )}
                {ProjectManagementRoutes.map(({ path, element, children }) => (
                  <Route key={path} path={path} element={element}>
                    {children &&
                      children.map(
                        ({ path: childPath, element: childElement }) => (
                          <Route
                            key={childPath}
                            path={childPath}
                            element={childElement}
                          />
                        ),
                      )}
                  </Route>
                ))}
                {UsersManagementRoutes.map(({ path, element, children }) => (
                  <Route key={path} path={path} element={element}>
                    {children &&
                      children.map(
                        ({ path: childPath, element: childElement }) => (
                          <Route
                            key={childPath}
                            path={childPath}
                            element={childElement}
                          />
                        ),
                      )}
                  </Route>
                ))}
                {TemplateRoutes.map(({ path, element, children }) => (
                  <Route key={path} path={path} element={element}>
                    {children &&
                      children.map(
                        ({ path: childPath, element: childElement }) => (
                          <Route
                            key={childPath}
                            path={childPath}
                            element={childElement}
                          />
                        ),
                      )}
                  </Route>
                ))}
                {ExecutionConfigRoutes.map(({ path, element, children }) => (
                  <Route key={path} path={path} element={element}>
                    {children &&
                      children.map(
                        ({ path: childPath, element: childElement }) => (
                          <Route
                            key={childPath}
                            path={childPath}
                            element={childElement}
                          />
                        ),
                      )}
                  </Route>
                ))}
                <Route
                  path="/integration/"
                  element={<Navigate to="plugin" replace />}
                />
                {renderRoutes(IntegrationRoutes as IntegrationRoute[])}
                <Route
                  path="/dashboard/*"
                  element={
                    <Dashboard
                      privilegeData={getPrivilegeStateData(privilegeData)}
                      privilegeDetails={privilegeData.privilegeDetails}
                    />
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
      {isChatbotVisible && <ChatBot />}
    </>
  );
};

export default Home;
